import {
  Box,
  Typography,
  useTheme,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Alert,
  TablePagination,
  tableCellClasses,
  styled,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { useState, useEffect } from "react";
import Refresh from "@mui/icons-material/Refresh";
import VisibilityIcon from '@mui/icons-material/Visibility';
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext.jsx";
import { getOrganizationName } from "../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import { getByLabId } from "../../services/PatientlabTestService";
import EditPatientLabTest from "../inPatientDetail/editPatientLabTests";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";

const columns = [
  { id: "id", label: "Report Id" },
  { id: "patientName", label: "Patient Name" },
  { id: "testName", label: "Test Name" },
  { id: "testResult", label: "Test Result" },
  { id: "testStatus", label: "Test Status" },
  { id: "testDate", label: "Report Date" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const AllPatientTest = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState(true);
  const [labTests, setLabTests] = useState([]);
  const [labTestInfo, setLabTestInfo] = useState(null);
  const [openLabTestModal, setOpenLabTestModal] = useState(false);
  const theme = useTheme();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPatientName, setFilterPatientName] = useState("");
  const [filterTestName, setFilterTestName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchPatientTestByLabId();
  }, []);

  // const fetchPatientTestByLabId = async () => {
  //   setFetching(true);
  //   try {
  //     const data = await getByLabId(user.id);
  //     console.log("Fetched lab tests:", data);
  //     setLabTests(data || []);
  //   } catch (error) {
  //     console.error("Error fetching lab tests:", error);
  //   } finally {
  //     setFetching(false);
  //   }
  // };

  const fetchPatientTestByLabId = async () => {
    setFetching(true);
    try {
      const data = await getByLabId(user.id);

      // Group by patientId: keep latest test per patient
      const grouped = Object.values(
        data.reduce((acc, curr) => {
          const key = curr.patientId;
          const existing = acc[key];

          const currentDate = Array.isArray(curr.testDate)
            ? new Date(...curr.testDate)
            : new Date(curr.testDate);

          if (!existing) {
            acc[key] = curr;
          } else {
            const existingDate = Array.isArray(existing.testDate)
              ? new Date(...existing.testDate)
              : new Date(existing.testDate);

            if (currentDate > existingDate) {
              acc[key] = curr;
            }
          }

          return acc;
        }, {})
      );

      const sorted = [...grouped].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      setLabTests(sorted);
    } catch (error) {
      console.error("Error fetching lab tests:", error);
    } finally {
      setFetching(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const filteredRows = (labTests || [])
    .filter((row) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const vals = [row.id, row.patientName, row.testName, row.testResult, row.testStatus, row.testDate]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        if (!vals.some((v) => v.includes(q))) return false;
      }
      if (filterPatientName.trim()) {
        if (!String(row.patientName || "").toLowerCase().includes(filterPatientName.toLowerCase())) return false;
      }
      if (filterTestName.trim()) {
        if (!String(row.testName || "").toLowerCase().includes(filterTestName.toLowerCase())) return false;
      }
      if (statusFilter) {
        if (String(row.testStatus || "") !== statusFilter) return false;
      }
      if (fromDate || toDate) {
        const d = Array.isArray(row.testDate) ? new Date(...row.testDate) : (row.testDate ? new Date(row.testDate) : null);
        if (!d) return false;
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate) { const toEnd = new Date(toDate); toEnd.setHours(23,59,59,999); if (d > toEnd) return false; }
      }
      return true;
    })
    .sort((a, b) => (b.id ?? 0) - (a.id ?? 0));

  const clearFilters = () => {
    setSearchQuery("");
    setFilterPatientName("");
    setFilterTestName("");
    setStatusFilter("");
    setFromDate("");
    setToDate("");
    setPage(0);
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["Report Id", "Patient Name", "Test Name", "Test Result", "Test Status", "Report Date"];
    const tableRows = rows.map((r) => [
      r.id,
      r.patientName,
      r.testName,
      r.testResult,
      r.testStatus,
      r.testDate
        ? dayjs(Array.isArray(r.testDate) ? new Date(...r.testDate) : new Date(r.testDate)).format("YYYY-MM-DD")
        : "",
    ]);

    await downloadStyledTableExcel({
      filename: `ipd_path_patients_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "IPD Path Patients",
      title: "IPD Path Patients",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="space-between"
        // mb="5px"
      >
        <Typography variant="h4" fontWeight="bold">
          IPD Path Patients
        </Typography>
        <Box display="flex" alignItems="center">
          <IconButton onClick={fetchPatientTestByLabId}>
            <Refresh />
          </IconButton>
        </Box>
      </Grid>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 2, pt: 1 }}>
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField placeholder="Search" value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)} fullWidth InputProps={{ startAdornment:(<InputAdornment position="start"><SearchIcon color="action"/></InputAdornment>) }} />
            <Tooltip title={showFilters?"Hide filters":"Show filters"}><IconButton onClick={()=>setShowFilters(v=>!v)}><FilterAltIcon color={showFilters?"primary":"inherit"}/></IconButton></Tooltip>
            <Tooltip title="Export to Excel"><IconButton onClick={handleExport}><DownloadIcon/></IconButton></Tooltip>
          </Stack>
          {showFilters && (
          <Stack spacing={1.5} direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} sx={{ mt:1 }}>
            <TextField label="Patient name" value={filterPatientName} onChange={(e)=>setFilterPatientName(e.target.value)} />
            <TextField label="Test name" value={filterTestName} onChange={(e)=>setFilterTestName(e.target.value)} />
            <TextField label="Status" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} select sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {[...new Set(labTests.map(r=>r.testStatus).filter(Boolean))].map(s=> (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField label="From date" type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <TextField label="To date" type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <Tooltip title="Clear filters"><IconButton onClick={clearFilters}><ClearAllIcon/></IconButton></Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell key={column.id}>
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Alert severity="info">No Test Records Found</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover key={row.id}>
                    {columns.map((column) => {
                      let value = row[column.id];
                      if (column.id === "testDate" && value) {
                        const dateObj = Array.isArray(value)
                          ? new Date(...value)
                          : new Date(value);
                        value = dayjs(dateObj).format("D MMM YYYY");
                      }
                      return (
                        <TableCell key={column.id}>
                          {value || "-"}
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      <IconButton
                        sx={{ color: theme.palette.primary.main }}
                        onClick={() => {
                          setOpenLabTestModal(true);
                          setLabTestInfo(row);
                        }}
                      >
                        <ModeEditOutlineTwoToneIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={labTests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <EditPatientLabTest
        open={openLabTestModal}
        onClose={async () => {
          setOpenLabTestModal(false);
          setLabTestInfo(null);
          await fetchPatientTestByLabId();
        }}
        patientId={labTestInfo?.patientId}
        labId={user?.id}
      />
    </Grid>
  );
};

export default AllPatientTest;